import {
  LoaderCircle,
  Save,
} from 'lucide-react'
import {
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

import ModalMensaje from '../ui/ModalMensaje'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { registrarCompraPrevia } from '../../services/abastecimiento'
import {
  calcularMontoEstimado,
  validarCompraPrevia,
} from '../../utils/abastecimiento'
import {
  formatearMontoCompra,
  nombreProveedorCompra,
} from '../../utils/compras'
import { obtenerFechaActualPos } from '../../utils/pos'
import {
  CampoModal,
  estiloInputModal,
} from './ModalBase'

// Compra PREVIA: solo acuerdo comercial. No lleva pesajes ni genera
// inventario ni gasto; el monto es una estimación hasta validar la
// recepción.
function FormularioCompraPrevia({
  empresaId,
  proveedores,
  productos,
}) {
  const navigate = useNavigate()
  const { modal, abrirModal, cerrarModal } = useModalMensaje()

  const [proveedorId, setProveedorId] = useState('')
  const [fecha, setFecha] = useState(obtenerFechaActualPos())
  const [productoId, setProductoId] = useState('')
  const [kilosAcordados, setKilosAcordados] = useState('')
  const [precioKilo, setPrecioKilo] = useState('')
  const [condicion, setCondicion] = useState('CONTADO')
  const [plazoDias, setPlazoDias] = useState('')
  const [documento, setDocumento] = useState('')
  const [observaciones, setObservaciones] = useState('')
  const [registrando, setRegistrando] = useState(false)
  const enCursoRef = useRef(false)

  const montoEstimado = calcularMontoEstimado(kilosAcordados, precioKilo)

  const registrar = async () => {
    if (enCursoRef.current) return

    const { error, valores } = validarCompraPrevia({
      proveedorId,
      fecha,
      productoId,
      kilosAcordados,
      precioKilo,
      condicion,
      plazoDias,
    })

    if (error) {
      abrirModal({
        tipo: 'error',
        titulo: 'Revisa la compra',
        mensaje: error,
      })
      return
    }

    enCursoRef.current = true
    setRegistrando(true)

    try {
      const compraId = await registrarCompraPrevia({
        empresaId,
        proveedorId,
        fecha,
        productoId,
        precioKilo: valores.precio,
        kilosAcordados: valores.kilos,
        condicion: valores.condicion,
        plazoDias: valores.plazo,
        observaciones: observaciones.trim(),
        documento: documento.trim(),
      })

      abrirModal({
        tipo: 'success',
        titulo: 'Compra previa registrada',
        mensaje:
          'Quedó pendiente de recepción. No se generó inventario ni gasto.',
        textoAceptar: 'Ver compra',
        onAceptar: () => {
          cerrarModal()
          navigate(`/compras/${compraId}`, { replace: true })
        },
      })
    } catch (errorRegistro) {
      console.error('Error al registrar compra previa:', errorRegistro)

      abrirModal({
        tipo: 'error',
        titulo: 'No fue posible registrar la compra',
        mensaje:
          errorRegistro.message ||
          'Ocurrió un error al registrar la compra previa.',
      })
      // Solo se libera ante error: tras un éxito el formulario no debe
      // poder reenviarse (evita duplicar la compra con doble clic).
      enCursoRef.current = false
      setRegistrando(false)
    }
  }

  return (
    <section className="max-w-3xl rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
        <CampoModal
          label="Proveedor / Agricultor"
          htmlFor="previa-proveedor"
          requerido
          className="md:col-span-2"
        >
          <select
            id="previa-proveedor"
            value={proveedorId}
            onChange={(event) => setProveedorId(event.target.value)}
            disabled={registrando}
            className={estiloInputModal}
          >
            <option value="">Seleccionar proveedor</option>
            {proveedores.map((proveedor) => (
              <option
                key={proveedor.id}
                value={proveedor.id}
              >
                {nombreProveedorCompra(proveedor)}
              </option>
            ))}
          </select>
        </CampoModal>

        <CampoModal
          label="Fecha"
          htmlFor="previa-fecha"
          requerido
        >
          <input
            id="previa-fecha"
            type="date"
            value={fecha}
            onChange={(event) => setFecha(event.target.value)}
            disabled={registrando}
            className={estiloInputModal}
          />
        </CampoModal>

        <CampoModal
          label="Producto"
          htmlFor="previa-producto"
          requerido
        >
          <select
            id="previa-producto"
            value={productoId}
            onChange={(event) => setProductoId(event.target.value)}
            disabled={registrando}
            className={estiloInputModal}
          >
            <option value="">Seleccionar producto</option>
            {productos.map((producto) => (
              <option
                key={producto.id}
                value={producto.id}
              >
                {producto.nombre}
              </option>
            ))}
          </select>
        </CampoModal>

        <CampoModal
          label="Kilos acordados"
          htmlFor="previa-kilos"
          requerido
        >
          <input
            id="previa-kilos"
            type="text"
            inputMode="decimal"
            value={kilosAcordados}
            onChange={(event) => setKilosAcordados(event.target.value)}
            placeholder="2500"
            disabled={registrando}
            className={estiloInputModal}
          />
        </CampoModal>

        <CampoModal
          label="Precio/kg"
          htmlFor="previa-precio"
          requerido
        >
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-slate-400">
              $
            </span>

            <input
              id="previa-precio"
              type="text"
              inputMode="decimal"
              value={precioKilo}
              onChange={(event) => setPrecioKilo(event.target.value)}
              placeholder="750"
              disabled={registrando}
              className={`${estiloInputModal} pl-7`}
            />
          </div>
        </CampoModal>

        <CampoModal
          label="Condición de pago"
          htmlFor="previa-condicion"
          requerido
        >
          <select
            id="previa-condicion"
            value={condicion}
            onChange={(event) => {
              setCondicion(event.target.value)
              setPlazoDias('')
            }}
            disabled={registrando}
            className={estiloInputModal}
          >
            <option value="CONTADO">Contado</option>
            <option value="CRÉDITO">Crédito</option>
          </select>
        </CampoModal>

        {condicion === 'CRÉDITO' && (
          <CampoModal
            label="Plazo (días corridos)"
            htmlFor="previa-plazo"
            requerido
          >
            <input
              id="previa-plazo"
              type="text"
              inputMode="numeric"
              value={plazoDias}
              onChange={(event) => setPlazoDias(event.target.value)}
              placeholder="30"
              disabled={registrando}
              className={estiloInputModal}
            />
          </CampoModal>
        )}

        <CampoModal
          label="Documento"
          htmlFor="previa-documento"
        >
          <input
            id="previa-documento"
            type="text"
            value={documento}
            onChange={(event) => setDocumento(event.target.value)}
            placeholder="Opcional"
            maxLength={120}
            disabled={registrando}
            className={estiloInputModal}
          />
        </CampoModal>

        <CampoModal
          label="Observaciones"
          htmlFor="previa-observaciones"
          className="md:col-span-2"
        >
          <textarea
            id="previa-observaciones"
            value={observaciones}
            onChange={(event) => setObservaciones(event.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="Opcional"
            disabled={registrando}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-base outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
          />
        </CampoModal>
      </div>

      <div className="mt-5 flex items-center justify-between gap-4 rounded-xl bg-emerald-50 px-4 py-3">
        <div>
          <span className="text-sm font-bold uppercase tracking-wide text-emerald-800">
            Monto estimado
          </span>

          <p className="text-xs text-emerald-700">
            Kilos acordados × precio/kg. No es el importe definitivo.
          </p>
        </div>

        <span className="text-right text-xl font-bold tabular-nums text-emerald-700">
          {montoEstimado === null ? '—' : formatearMontoCompra(montoEstimado)}
        </span>
      </div>

      <button
        type="button"
        onClick={registrar}
        disabled={registrando}
        className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:ml-auto sm:w-auto"
      >
        {registrando ? (
          <LoaderCircle
            size={18}
            className="animate-spin"
          />
        ) : (
          <Save size={18} />
        )}
        Guardar compra previa
      </button>

      <ModalMensaje {...modal} />
    </section>
  )
}

export default FormularioCompraPrevia
